// src/components/i3m/ReportGenerator.js

import React, { useEffect, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import TextAlign from '@tiptap/extension-text-align';
import { FaArrowLeft, FaPrint, FaImage, FaAlignLeft, FaAlignCenter, FaAlignRight, FaAlignJustify } from 'react-icons/fa';
import '../tools.css';
import './ReportGenerator.css';

const ReportGenerator = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Extract data from location state
  const {
    subImages = [],
    annotatedImagesData = {},
    imageFile = null,
    userEmail = '',
    tipo = '',
    posizione = '',
    sesso = '',
    regione = '',
    realAge = '',
    userInfo = {},
    allAnnotations = {},
    i3mL = null,
    i3mR = null,
    agei3mL = null,
    agei3mR = null,
    cutoffI3ML = null,
    cutoffI3MR = null,
    // New probability calculation data
    probabilityResults = null,
    combinedResults = null,
  } = location.state || {};

  // Initialize TipTap Editor
  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        link: {
          openOnClick: false,
        },
      }),
      Image.configure({
        allowBase64: true,
        HTMLAttributes: {
          class: 'report-image',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
    ],
    content: '',
  });

  // Generate report content function with useCallback to avoid dependency issues
  const generateReportHTML = useCallback(() => {
    // Debug log all input data
    console.log('=== ReportGenerator Data ===');
    console.log('imageFile:', imageFile);
    console.log('subImages:', subImages);
    console.log('allAnnotations:', allAnnotations);
    console.log('agei3mL:', agei3mL, 'agei3mR:', agei3mR);
    console.log('i3mL:', i3mL, 'i3mR:', i3mR);
    console.log('probabilityResults:', probabilityResults);
    console.log('combinedResults:', combinedResults);
    console.log('========================');

    // Extract date and time separately for professional formatting
    const now = new Date();
    const reportDate = now.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
    const reportTime = now.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });

    const professionalsName = userInfo?.name || '';
    const professionalTitle = userInfo?.title || 'Trainer / Researcher';
    const professionalPhone = userInfo?.phone || '';
    const professionalOrganization = userInfo?.organization || '';

    // Format professional header section
    const professionalHeaderSection = `
      <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #4CAF50; padding-bottom: 20px;">
        <div style="background-color: #7a3a00; color: #ffd180; border: 2px solid #ff9800; border-radius: 6px; padding: 10px 18px; margin-bottom: 18px; font-size: 0.95em; font-weight: bold; letter-spacing: 0.04em;">
          ⚠ FOR EDUCATIONAL AND TRAINING PURPOSES ONLY — NOT FOR USE IN REAL FORENSIC, CLINICAL, OR LEGAL PROCEEDINGS
        </div>
        <h1 style="margin: 0 0 10px 0;">I₃M Educational Analysis — Training Report</h1>
        <p style="margin: 5px 0; font-style: italic; color: #b0bec5;">Third Molar Maturity Index (I₃M) — Training Exercise</p>
        <hr style="margin: 15px 0; border: none; border-top: 1px solid #555;" />
      </div>
    `;

    // Format professional info section
    const userInfoSection = `
      <h2>Professional Information</h2>
      <div style="background-color: #2d2d2d; padding: 15px; border-radius: 4px; border-left: 4px solid #4CAF50;">
        <p style="margin: 5px 0;"><strong>Professional Name:</strong> ${professionalsName || '_____________________'}</p>
        <p style="margin: 5px 0;"><strong>Title/Position:</strong> ${professionalTitle}</p>
        <p style="margin: 5px 0;"><strong>Email:</strong> ${userEmail}</p>
        <p style="margin: 5px 0;"><strong>Phone:</strong> ${professionalPhone || '_____________________'}</p>
        <p style="margin: 5px 0;"><strong>Institution:</strong> ${professionalOrganization || '_____________________'}</p>
      </div>

      <h2 style="margin-top: 25px;">Sample Information</h2>
      <div style="background-color: #2d2d2d; padding: 15px; border-radius: 4px;">
        <p style="margin: 5px 0;"><strong>Sample Gender:</strong> ${sesso || 'Not Specified'}</p>
        <p style="margin: 5px 0;"><strong>Tooth Type:</strong> ${tipo || '_____________________'}</p>
        <p style="margin: 5px 0;"><strong>Position/Location:</strong> ${posizione || '_____________________'}</p>
        ${realAge ? `<p style="margin: 5px 0;"><strong>Reference Age (anonymised sample):</strong> ${realAge} years</p>` : ''}
      </div>

      <h2 style="margin-top: 25px;">Report Information</h2>
      <div style="background-color: #2d2d2d; padding: 15px; border-radius: 4px;">
        <p style="margin: 5px 0;"><strong>Report Date:</strong> ${reportDate}</p>
        <p style="margin: 5px 0;"><strong>Report Time:</strong> ${reportTime}</p>
        <p style="margin: 5px 0;"><strong>Report ID:</strong> ${now.getTime()}</p>
      </div>
    `;

    // Format images section
    let imagesSection = '<h2>Original Images</h2>';
    if (imageFile && imageFile.dataURL) {
      imagesSection += `<img src="${imageFile.dataURL}" alt="Original Image" style="width: 80vw; max-width: 80vw; height: auto; margin: 20px 0; border: 1px solid #ddd; padding: 10px;" />`;
      console.log('Original image found:', { imageName: imageFile.name, hasDataURL: !!imageFile.dataURL });
    } else {
      imagesSection += '<p><em>No original image provided.</em></p>';
      console.log('Original image NOT found. imageFile:', imageFile);
    }

    // Format annotated images and annotations
    let annotationsSection = '<h2>Detailed Annotations</h2>';

    subImages.forEach((subImage, index) => {
      const subImageId = subImage.id;
      const annotation = allAnnotations[subImageId] || {};
      const coordinates = annotation.coordinates || {};
      const ratios = annotation.ratios || {};

      // Debug: log annotation structure
      console.log(`Sub-image ${index} (ID: ${subImageId}):`, {
        annotation,
        coordinates,
        ratios,
        allAnnotationsKeys: Object.keys(allAnnotations),
      });

      // Determine if Left or Right based on filename
      const fileName = subImage.subImage?.name || 'Unknown';
      const isMolarLeft = fileName.includes('_L') || fileName.toLowerCase().includes('left');
      const isMolarRight = fileName.includes('_R') || fileName.toLowerCase().includes('right');
      const molarTitle = isMolarLeft ? 'Lower Left Third Molar Analysis' : 
                         isMolarRight ? 'Lower Right Third Molar Analysis' : 
                         `Sub-Image ${index + 1}`;

      annotationsSection += `
        <h3>${molarTitle}</h3>
        <p style="font-size: 0.9em; color: #b0bec5;">File: ${fileName}</p>
      `;

      // Display both original and annotated images side by side
      const hasAnnotatedImage = annotatedImagesData && annotatedImagesData[subImageId];
      
      if (subImage.subImage?.dataURL || hasAnnotatedImage) {
        annotationsSection += '<div style="display: flex; gap: 20px; margin: 15px 0; flex-wrap: wrap;">';
        
        // Original/Raw image
        if (subImage.subImage?.dataURL) {
          annotationsSection += `
            <div style="flex: 1; min-width: 250px;">
              <p style="text-align: center; font-weight: bold; margin-bottom: 10px;">Original Image</p>
              <img src="${subImage.subImage.dataURL}" alt="Original Sub-Image ${index + 1}" 
                   style="max-width: 100%; height: auto; border: 1px solid #555; border-radius: 4px; padding: 8px; background-color: #2d2d2d;" />
            </div>
          `;
        }
        
        // Annotated image
        if (hasAnnotatedImage) {
          annotationsSection += `
            <div style="flex: 1; min-width: 250px;">
              <p style="text-align: center; font-weight: bold; margin-bottom: 10px;">Annotated Image</p>
              <img src="${annotatedImagesData[subImageId]}" alt="Annotated Sub-Image ${index + 1}" 
                   style="max-width: 100%; height: auto; border: 1px solid #4CAF50; border-radius: 4px; padding: 8px; background-color: #2d2d2d;" />
            </div>
          `;
        }
        
        annotationsSection += '</div>';
      }

      annotationsSection += '<h4>Coordinates and Measurements</h4>';

      // Check if there are any coordinates
      const hasCoordinates = Object.values(coordinates).some(points => Array.isArray(points) && points.length > 0);
      
      if (!hasCoordinates) {
        annotationsSection += '<p><em>No annotations found for this image.</em></p>';
      } else {
        annotationsSection += '<ul>';
        // Format coordinates by line/color
        Object.entries(coordinates).forEach(([lineKey, points]) => {
          const colorName = getColorName(lineKey);
          annotationsSection += `<li><strong>${colorName}:</strong> `;

          if (Array.isArray(points) && points.length === 2) {
            const dx = points[1].x - points[0].x;
            const dy = points[1].y - points[0].y;
            const length = Math.sqrt(dx * dx + dy * dy);

            annotationsSection += `
              Point 1: (${Math.round(points[0].x)}, ${Math.round(points[0].y)}), 
              Point 2: (${Math.round(points[1].x)}, ${Math.round(points[1].y)}), 
              Length: ${Math.round(length)} px
            `;
          } else if (Array.isArray(points) && points.length === 1) {
            annotationsSection += `Point 1: (${Math.round(points[0].x)}, ${Math.round(points[0].y)})`;
          } else {
            annotationsSection += 'No measurements';
          }

          annotationsSection += '</li>';
        });
        annotationsSection += '</ul>';
      }

      // Format ratios if available
      if (Object.keys(ratios).length > 0) {
        const hasRatios = Object.values(ratios).some(value => value !== null && value !== undefined);
        if (hasRatios) {
          annotationsSection += '<h4>Measurements Summary</h4><ul>';
          Object.entries(ratios).forEach(([lineKey, value]) => {
            if (value !== null && value !== undefined) {
              const colorName = getColorName(lineKey);
              annotationsSection += `<li><strong>${colorName}:</strong> ${Math.round(value)} px</li>`;
            }
          });
          annotationsSection += '</ul>';
        }
      }
    });

    // Age estimation section with probability calculations
    let ageSection = '';
    if (agei3mL !== null || agei3mR !== null || i3mL !== null || i3mR !== null) {
      // Safe number conversion helper
      const safeToFixed = (value, decimals = 2) => {
        const num = typeof value === 'string' ? parseFloat(value) : value;
        return (num !== null && num !== undefined && !isNaN(num)) ? num.toFixed(decimals) : 'N/A';
      };

      ageSection = `
        <h2>Age Estimation Results</h2>
        <p><strong>Methodology:</strong> Third Molar Maturity Index (I₃M) with Bayesian Gaussian Model</p>
        <p><strong>Analysis Region:</strong> ${regione || 'Not Specified'}</p>
        <p><strong>Subject Gender:</strong> ${sesso || 'Not Specified'}</p>
      `;

      // Left Third Molar Results
      if (i3mL !== null || agei3mL !== null) {
        const leftProb = probabilityResults?.left;
        const leftPAdult = leftProb?.pAdult;
        const leftPMinor = leftProb?.pMinor;
        const hasValidLeftProb = leftPAdult !== null && leftPAdult !== undefined && !isNaN(leftPAdult);
        
        ageSection += `
          <h3 style="margin-top: 20px; color: #4CAF50;">Left Third Molar (I₃ML)</h3>
          <div style="background-color: #2d2d2d; padding: 15px; border-radius: 4px; border-left: 4px solid #2196F3;">
            <p style="margin: 5px 0;"><strong>I₃M Value:</strong> ${safeToFixed(i3mL)}</p>
            ${cutoffI3ML ? `<p style="margin: 5px 0;"><strong>Regional Cutoff:</strong> ${cutoffI3ML}</p>` : ''}
            <p style="margin: 5px 0;"><strong>Traditional Assessment:</strong> ${agei3mL || 'N/A'}</p>
            ${hasValidLeftProb ? `
              <p style="margin: 10px 0 5px 0; font-weight: bold; color: #4CAF50;">Probability Analysis:</p>
              <p style="margin: 5px 0 5px 15px;"><strong>P(Adult | I₃M):</strong> ${safeToFixed(leftPAdult * 100, 1)}%</p>
              <p style="margin: 5px 0 5px 15px;"><strong>P(Minor | I₃M):</strong> ${safeToFixed(leftPMinor * 100, 1)}%</p>
              <p style="margin: 5px 0 5px 15px;"><strong>Probability Decision:</strong> ${leftProb.decision || agei3mL}</p>
              ${leftProb?.interpolated ? `<p style="margin: 5px 0 5px 15px; font-size: 0.9em; color: #b0bec5;"><em>* Value extrapolated (I₃M > 0.20)</em></p>` : ''}
            ` : ''}
          </div>
        `;
      }

      // Right Third Molar Results
      if (i3mR !== null || agei3mR !== null) {
        const rightProb = probabilityResults?.right;
        const rightPAdult = rightProb?.pAdult;
        const rightPMinor = rightProb?.pMinor;
        const hasValidRightProb = rightPAdult !== null && rightPAdult !== undefined && !isNaN(rightPAdult);
        
        ageSection += `
          <h3 style="margin-top: 20px; color: #4CAF50;">Right Third Molar (I₃MR)</h3>
          <div style="background-color: #2d2d2d; padding: 15px; border-radius: 4px; border-left: 4px solid #2196F3;">
            <p style="margin: 5px 0;"><strong>I₃M Value:</strong> ${safeToFixed(i3mR)}</p>
            ${cutoffI3MR ? `<p style="margin: 5px 0;"><strong>Regional Cutoff:</strong> ${cutoffI3MR}</p>` : ''}
            <p style="margin: 5px 0;"><strong>Traditional Assessment:</strong> ${agei3mR || 'N/A'}</p>
            ${hasValidRightProb ? `
              <p style="margin: 10px 0 5px 0; font-weight: bold; color: #4CAF50;">Probability Analysis:</p>
              <p style="margin: 5px 0 5px 15px;"><strong>P(Adult | I₃M):</strong> ${safeToFixed(rightPAdult * 100, 1)}%</p>
              <p style="margin: 5px 0 5px 15px;"><strong>P(Minor | I₃M):</strong> ${safeToFixed(rightPMinor * 100, 1)}%</p>
              <p style="margin: 5px 0 5px 15px;"><strong>Probability Decision:</strong> ${rightProb.decision || agei3mR}</p>
              ${rightProb?.interpolated ? `<p style="margin: 5px 0 5px 15px; font-size: 0.9em; color: #b0bec5;"><em>* Value extrapolated (I₃M > 0.20)</em></p>` : ''}
            ` : ''}
          </div>
        `;
      }

      // Combined Results Section
      if (combinedResults && combinedResults.probMedia !== null) {
        ageSection += `
          <h3 style="margin-top: 25px; color: #FF9800;">Combined Analysis (Both Teeth)</h3>
          <div style="background-color: #2d2d2d; padding: 15px; border-radius: 4px; border-left: 4px solid #FF9800;">
            <p style="margin: 5px 0;"><strong>Average P(Adult):</strong> ${safeToFixed(combinedResults.probMedia * 100, 1)}%</p>
            <p style="margin: 5px 0;"><strong>Average P(Minor):</strong> ${safeToFixed((1 - combinedResults.probMedia) * 100, 1)}%</p>
            <p style="margin: 5px 0;"><strong>Final Classification:</strong> ${combinedResults.decisionMedia === 'Adult' ? '≥18 years (Adult)' : '<18 years (Minor)'}</p>
          </div>
        `;

        // Max Protection Principle Section
        const maxProtDecision = combinedResults.decisionMaxProtection || combinedResults.maxProtectionDecision;
        const isMinor = maxProtDecision === 'Minor';
        const explanation = isMinor 
          ? 'At least one tooth shows P(Adult) < 50%, subject classified as minor for protection.'
          : 'Both teeth show P(Adult) ≥ 50%, subject classified as adult.';
        
        ageSection += `
          <h3 style="margin-top: 25px; color: #f44336;">Final Assessment - Precautionary Principle</h3>
          <div style="background-color: ${isMinor ? '#2d1b1b' : '#1b2d1b'}; padding: 20px; border-radius: 4px; border: 2px solid ${isMinor ? '#f44336' : '#4CAF50'};">
            <p style="margin: 5px 0; font-size: 1.2em; font-weight: bold; text-align: center;">
              ${isMinor ? '⚠️ CLASSIFIED AS MINOR (<18 years)' : '✓ CLASSIFIED AS ADULT (≥18 years)'}
            </p>
            <p style="margin: 15px 0 5px 0; font-size: 0.95em; text-align: center; color: #b0bec5;">
              <strong>Explanation:</strong> ${explanation}
            </p>
            <hr style="margin: 15px 0; border: none; border-top: 1px solid #555;" />
            <p style="margin: 5px 0; font-size: 0.85em; color: #999; text-align: center;">
              <em>The Precautionary Principle ensures that if ANY tooth indicates a probability of being minor ≥50%, 
              the subject is classified as a minor to protect their rights.</em>
            </p>
          </div>
        `;
      }
    }

    // Bibliography section
    const bibliographySection = `
      <h2>Notes</h2>
      <div style="background-color: #2d2d2d; padding: 15px; border-radius: 4px; border: 1px dashed #4CAF50; min-height: 100px;">
        <p style="margin: 5px 0; color: #b0bec5;"><em>Additional notes and observations:</em></p>
        <p style="margin: 15px 0; line-height: 1.8;">_________________________________________________________________</p>
        <p style="margin: 5px 0; line-height: 1.8;">_________________________________________________________________</p>
        <p style="margin: 5px 0; line-height: 1.8;">_________________________________________________________________</p>
      </div>

      <h2 style="margin-top: 25px;">References</h2>
      <p>
        Angelakopoulos N., De Luca S., Oliveira-Santos I. et al. "Third molar maturity index (I₃M) assessment 
        according to different geographical zones: a large multi-ethnic study sample." 
        <em>International Journal of Legal Medicine</em> (2023, published December 2022).
      </p>
      <div style="background-color: #7a3a00; color: #ffd180; border: 1px solid #ff9800; border-radius: 4px; padding: 12px 16px; margin-top: 10px;">
        <strong>⚠ Educational Disclaimer:</strong> This document is generated exclusively for training and educational
        purposes within supervised academic or professional development contexts. It does not constitute a forensic
        expert opinion, a clinical report, or legal evidence of any kind. All age estimations are based on
        anonymised sample images and are provided solely to illustrate the I₃M methodology. Results must not
        be used to support real-case decisions.
      </div>
    `;

    const fullReport = `
      ${professionalHeaderSection}
      
      ${userInfoSection}
      
      ${imagesSection}
      
      ${annotationsSection}
      
      ${ageSection}
      
      ${bibliographySection}
      
      <h2 style="margin-top: 40px;">Certification and Signature</h2>
      <div style="margin-top: 30px; padding: 20px; background-color: #2d2d2d; border-radius: 4px;">
        <p style="margin-bottom: 20px;">
          I hereby certify that I have personally reviewed this case and performed the analysis according to the 
          <strong>Third Molar Maturity Index (I₃M)</strong> methodology as described in the referenced literature.
        </p>
        
        <p style="margin-bottom: 20px;">
          I confirm that I have completed this training exercise using anonymised sample images and that this
          document is produced exclusively for educational purposes within a supervised training context.
        </p>

        <div style="margin-top: 40px; display: grid; grid-template-columns: 1fr 1fr; gap: 40px;">
          <div>
            <p style="margin-bottom: 30px;"><strong>Trainer / Participant Signature:</strong></p>
            <div style="border-top: 2px solid #666; height: 60px; width: 100%;"></div>
            <p style="margin-top: 10px; font-size: 0.9em; color: #b0bec5;">${professionalsName || '_____________________'}</p>
          </div>

          <div>
            <p style="margin-bottom: 30px;"><strong>Date:</strong></p>
            <div style="border-top: 2px solid #666; height: 60px; width: 100%;"></div>
            <p style="margin-top: 10px; font-size: 0.9em; color: #b0bec5;">_____/_____/_______</p>
          </div>
        </div>
      </div>

      <footer style="margin-top: 40px; padding-top: 20px; border-top: 1px solid #555; font-size: 0.85em; color: #999; text-align: center;">
        <p style="color: #ff9800; font-weight: bold;">FOR EDUCATIONAL USE ONLY — NOT FOR USE IN FORENSIC, CLINICAL, OR LEGAL PROCEEDINGS</p>
        <p>Training Report ID: ${now.getTime()} | Generated: ${reportDate} at ${reportTime}</p>
        <p>AGEstimation Project — ageestimation.netlify.app</p>
      </footer>
    `;

    return fullReport;
  }, [subImages, annotatedImagesData, imageFile, userEmail, tipo, posizione, sesso, regione, realAge, userInfo, allAnnotations, i3mL, i3mR, agei3mL, agei3mR, cutoffI3ML, cutoffI3MR, probabilityResults, combinedResults]);

  // Generate report content on component mount
  useEffect(() => {
    if (editor && subImages.length > 0) {
      const reportHTML = generateReportHTML();
      editor.commands.setContent(reportHTML);
    }
  }, [editor, subImages, generateReportHTML]);

  const getColorName = (lineKey) => {
    const colorMap = {
      line1: 'Blue - Tooth Height',
      line2: 'Red - Root Measurement',
      line3: 'Green - Root Measurement',
    };
    return colorMap[lineKey] || lineKey;
  };

  const handlePrint = () => {
    if (editor) {
      window.print();
    }
  };

  const handleAddImage = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    input.onchange = (event) => {
      const file = event.target.files?.[0];
      if (file) {
        const reader = new FileReader();
        reader.onload = (e) => {
          const src = e.target?.result;
          if (src && editor) {
            editor.chain().focus().setImage({ src }).run();
          }
        };
        reader.readAsDataURL(file);
      }
    };
    input.click();
  };

  const handleGoBack = () => {
    navigate(-1);
  };

  if (!editor) {
    return (
      <div className="report-generator-container">
        <p>Loading report editor...</p>
      </div>
    );
  }

  return (
    <div className="report-generator-container">
      <div className="report-header">
        <h1>Forensic Report Editor - I3M Analysis</h1>
        <div className="report-actions">
          <button onClick={handleGoBack} className="report-button back-button" title="Go Back">
            <FaArrowLeft /> Back
          </button>
          <button onClick={handlePrint} className="report-button print-button" title="Print Report">
            <FaPrint /> Print
          </button>
        </div>
      </div>

      <div className="report-toolbar">
        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBold().run()}
            disabled={!editor.can().chain().focus().toggleBold().run()}
            className={editor.isActive('bold') ? 'active' : ''}
          >
            <strong>B</strong>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleItalic().run()}
            disabled={!editor.can().chain().focus().toggleItalic().run()}
            className={editor.isActive('italic') ? 'active' : ''}
          >
            <em>I</em>
          </button>
          <button
            onClick={() => editor.chain().focus().toggleUnderline().run()}
            disabled={!editor.can().chain().focus().toggleUnderline().run()}
            className={editor.isActive('underline') ? 'active' : ''}
          >
            <u>U</u>
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
            className={editor.isActive('heading', { level: 1 }) ? 'active' : ''}
          >
            H1
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            className={editor.isActive('heading', { level: 2 }) ? 'active' : ''}
          >
            H2
          </button>
          <button
            onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            className={editor.isActive('heading', { level: 3 }) ? 'active' : ''}
          >
            H3
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().toggleBulletList().run()}
            className={editor.isActive('bulletList') ? 'active' : ''}
          >
            • List
          </button>
          <button
            onClick={() => editor.chain().focus().toggleOrderedList().run()}
            className={editor.isActive('orderedList') ? 'active' : ''}
          >
            1. List
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={() => editor.chain().focus().setTextAlign('left').run()}
            className={editor.isActive({ textAlign: 'left' }) ? 'active' : ''}
            title="Align Left"
          >
            <FaAlignLeft />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('center').run()}
            className={editor.isActive({ textAlign: 'center' }) ? 'active' : ''}
            title="Align Center"
          >
            <FaAlignCenter />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('right').run()}
            className={editor.isActive({ textAlign: 'right' }) ? 'active' : ''}
            title="Align Right"
          >
            <FaAlignRight />
          </button>
          <button
            onClick={() => editor.chain().focus().setTextAlign('justify').run()}
            className={editor.isActive({ textAlign: 'justify' }) ? 'active' : ''}
            title="Justify"
          >
            <FaAlignJustify />
          </button>
        </div>

        <div className="toolbar-group">
          <button
            onClick={handleAddImage}
            title="Insert Image"
          >
            <FaImage /> Image
          </button>
        </div>

        <div className="toolbar-group">
          <button onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
            ↶ Undo
          </button>
          <button onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
            ↷ Redo
          </button>
        </div>
      </div>

      <div className="editor-wrapper">
        <EditorContent editor={editor} className="report-editor" />
      </div>
    </div>
  );
};

export default ReportGenerator;
